<script setup lang="ts">
import {computed, ref} from "vue";
import dateHelper from "@/core/helpers/dateHelper";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {course} from "@/medication-checkup/course";
import {useCourseAutoScroll} from "@/medication-checkup/composables/useCourseAutoScroll";
import MedicationCheckupCurrentStepCard from "@/medication-checkup/components/MedicationCheckupCurrentStepCard.vue";
import MedicationCheckupPatientCard from "@/medication-checkup/components/MedicationCheckupPatientCard.vue";
import MedicationCheckupQuickReply from "@/medication-checkup/components/MedicationCheckupQuickReply.vue";
import {useNextStep} from "@/medication-checkup/composables/useNextStep";
import {MedicationCheckupBlock} from "@/medication-checkup/interfaces";

/**
 * The course of the cycle, played scene by scene, one step at a time, with
 * the step shown kept in view beside it.
 *
 * The thread is the thread of one step — the one being worked on by default, or
 * one already played, reopened to reread its record. Within it, every scene
 * up to and including the current one is rendered — the completed ones
 * frozen, as the trace the interview left; the current one live. What
 * follows stays unplayed: the thread ends where the course does — and, once
 * the step shown is over, on the reply that moves on to the next one.
 *
 * Nothing about the position in the course is decided here: a scene is frozen
 * when the server says its block is completed, and the current scene is simply
 * the first one that is not. Session resume is this same rule reading the
 * same served state, which is why reopening a cycle lands exactly where the
 * pharmacist left it. Which step is shown is the store's: the HDS composes
 * the views of one step per read, and the store knows which.
 *
 * The side column carries two cards — who the patient is, then where the
 * interview stands — and scrolls on its own, so they stay in view while the
 * thread runs beside them.
 */
const store = useMedicationCheckupStore();

/**
 * The scenes of the step shown, each paired with the served block it renders.
 *
 * A scene whose block the step does not hold is skipped — it belongs to
 * another step, or the pinned definition does not declare it: the definition
 * is what the course is made of, and this front may know scenes an older
 * pinned version does not have yet.
 */
const scenes = computed(() => {
    return course.flatMap(scene => {
        const block = store.displayedBlocks.find(candidate => candidate.definition_key === scene.key);

        return block === undefined ? [] : [{scene, block}];
    });
});

/**
 * How far the course is played: the completed scenes, then the current one.
 */
const played = computed(() => {
    const current = scenes.value.findIndex(({block}) => !block.is_completed);

    return current === -1 ? scenes.value : scenes.value.slice(0, current + 1);
});

/**
 * The day the interview is being held, as the thread heads itself.
 */
const today = computed(() => dateHelper.shortFormat(new Date(), 'dddd D MMMM YYYY'));

const {next, label: nextLabel, open: openNext} = useNextStep();

const viewport = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);

useCourseAutoScroll(viewport, content, {
    stepKey: computed(() => store.displayedStepKey),
    frozen: computed(() => store.displayedStep?.status === 'completed')
});

/**
 * The room left above a scene brought back into view — the padding of the
 * thread, so the scene lands where a freshly opened thread would place it.
 */
const JUMP_OFFSET_PX = 18;

/**
 * Bring the scene of a block back into view, from the checklist.
 *
 * Scrolling up is the pharmacist's move, so the thread stops following its end
 * exactly as it does on a manual scroll; coming back down resumes it.
 */
const jumpTo = (block: MedicationCheckupBlock) => {
    const element = viewport.value;
    const scene = content.value?.querySelector<HTMLElement>(`[data-block-id="${block.id}"]`);

    if (element === null || !scene) {
        return;
    }

    const top = scene.getBoundingClientRect().top
        - element.getBoundingClientRect().top
        + element.scrollTop
        - JUMP_OFFSET_PX;

    element.scrollTo({top, behavior: 'smooth'});
};
</script>

<template>
  <div v-if="store.cycle" class="flex h-full min-h-0">
    <aside class="w-[292px] shrink-0 overflow-y-auto py-4 pl-5 flex flex-col gap-3 pr-1">
      <MedicationCheckupPatientCard
        v-if="store.customerId"
        :customer-id="store.customerId"
      />
      <MedicationCheckupCurrentStepCard @jump="jumpTo" />
    </aside>

    <div
      ref="viewport"
      class="flex-1 min-w-0 min-h-0 overflow-y-auto px-6 pt-[18px] pb-6 transition-opacity duration-200 [container-type:inline-size]"
      :class="{'opacity-60 pointer-events-none': store.stepLoading}"
      :aria-busy="store.stepLoading"
    >
      <div ref="content" class="mx-auto w-full max-w-[760px] flex flex-col gap-3.5">
        <div
          class="self-center px-3 py-0.5 rounded-full bg-gray-light text-xs text-slate-500 first-letter:uppercase"
        >
          Copilote BPM · {{ today }}
        </div>

        <div
          v-for="{scene, block} in played"
          :key="scene.key"
          :data-block-id="block.id"
        >
          <component
            :is="scene.component"
            :block="block"
            :frozen="block.is_completed"
          />
        </div>

        <div v-if="next && nextLabel" class="flex justify-end animate-message-in">
          <MedicationCheckupQuickReply
            :label="`Passer à l'étape suivante : ${nextLabel}`"
            :disabled="store.stepLoading"
            @click="openNext"
          />
        </div>
      </div>
    </div>
  </div>
</template>
